import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Search, Loader2, Building2, MapPin, FileText, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { HistorialPanel } from './HistorialPanel';
import { BulkSearch } from './ArcaBulkSearch';

const cleanCuit = (v) => (v || '').replace(/\D/g, '');

// Normaliza las actividades declaradas desde el resumen del padrón (RG o monotributo).
const actividadesDe = (r) => {
  const list = (Array.isArray(r?.actividades) ? r.actividades : []).map((a) => ({
    desc: a.descripcion || '', cod: a.id ?? a.codigo ?? '',
    principal: String(a.orden) === '1' || String(a.orden) === '1.0',
  })).filter((a) => a.desc);
  if (list.length) return list;
  if (r?.monotributo?.actividad) return [{ desc: r.monotributo.actividad, cod: r.monotributo.actividad_id ?? '', principal: true }];
  return [];
};

const PadronDetalle = ({ data }) => {
  const r = data?.resumen || {};
  const dom = r.domicilio || {};
  return (
    <div className="grid md:grid-cols-2 gap-x-8">
      <div>
        <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">Razón social</span><span className="font-medium text-slate-800 text-right">{r.razon_social || '—'}</span></div>
        <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">Tipo de persona</span><span className="font-medium text-slate-800 text-right">{r.tipo_persona || '—'}</span></div>
        <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">Estado</span><span className="font-medium text-slate-800 text-right">{r.estado || '—'}</span></div>
        <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">Condición IVA</span><span className="font-medium text-slate-800 text-right">{r.condicion_iva || '—'}</span></div>
        {r.monotributo && <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">Monotributo</span><span className="font-medium text-slate-800 text-right">{r.monotributo.categoria}</span></div>}
      </div>
      <div>
        <div className="flex items-center gap-2 text-slate-600 text-sm mb-1"><MapPin size={14} /> Domicilio fiscal</div>
        <p className="text-sm text-slate-800">{dom.direccion || '—'}</p>
        <p className="text-sm text-slate-500">{[dom.localidad, dom.provincia, dom.codigo_postal].filter(Boolean).join(' · ')}</p>
        {actividadesDe(r).length > 0 && <p className="text-xs text-slate-500 mt-2">Act.: {(actividadesDe(r).find((a) => a.principal) || actividadesDe(r)[0]).desc}</p>}
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right">{value || '—'}</span>
  </div>
);

export default function ArcaConsulta() {
  const navigate = useNavigate();
  const [cuit, setCuit] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [dni, setDni] = useState('');
  const [dniLoading, setDniLoading] = useState(false);
  const [dniResult, setDniResult] = useState(null);

  const buscarDni = async (e) => {
    e?.preventDefault();
    const d = (dni || '').replace(/\D/g, '');
    if (d.length < 7 || d.length > 8) {
      toast.error('El DNI debe tener 7 u 8 dígitos');
      return;
    }
    setDniLoading(true); setDniResult(null);
    try {
      const res = await api.get(`/arca/dni/${d}`);
      setDniResult(res.data);
      if (!res.data?.cuits?.length) toast.info('Sin CUIT/CUIL para ese DNI');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error buscando por DNI');
    } finally {
      setDniLoading(false);
    }
  };

  const usarCuit = (c) => {
    setCuit(c);
    setTimeout(() => buscar(), 0);
  };

  const buscar = async (e) => {
    e?.preventDefault();
    const c = cleanCuit(cuit);
    if (c.length !== 11) {
      toast.error('El CUIT debe tener 11 dígitos');
      return;
    }
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await api.get(`/arca/consulta/${c}`);
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error consultando ARCA';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const r = result?.resumen || {};
  const dom = r.domicilio || {};

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="arca-consulta-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Consulta de Padrón ARCA</h1>
        <p className="text-slate-500 text-sm mt-1">Datos del contribuyente por CUIT (constancia de inscripción).</p>
      </div>

      <Tabs defaultValue="consulta">
        <TabsList data-testid="arca-consulta-tabs">
          <TabsTrigger value="consulta" data-testid="arca-consulta-tab-consulta">Consulta</TabsTrigger>
          <TabsTrigger value="masivo" data-testid="arca-consulta-tab-masivo">Masivo</TabsTrigger>
          <TabsTrigger value="historial" data-testid="arca-consulta-tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="consulta" className="space-y-6 mt-4">
      <Card className="p-5">
        <form onSubmit={buscar} className="flex items-center gap-3">
          <Input
            data-testid="arca-cuit-input"
            placeholder="CUIT (11 dígitos)"
            value={cuit}
            onChange={(e) => setCuit(e.target.value)}
            className="max-w-xs font-mono"
            maxLength={13}
          />
          <Button type="submit" disabled={loading} data-testid="arca-consultar-btn" className="bg-sky-600 hover:bg-sky-700">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
            {loading ? 'Consultando…' : 'Consultar'}
          </Button>
        </form>
      </Card>

      {/* Búsqueda inversa DNI → CUIT (Padrón A13) */}
      <Card className="p-5" data-testid="arca-dni-card">
        <p className="text-sm font-medium text-slate-700 mb-2">¿No tenés el CUIT? Buscalo por DNI <span className="text-xs text-slate-400">(Padrón A13)</span></p>
        <form onSubmit={buscarDni} className="flex items-center gap-3">
          <Input
            data-testid="arca-dni-input"
            placeholder="DNI (7 u 8 dígitos)"
            value={dni}
            onChange={(e) => setDni(e.target.value)}
            className="max-w-xs font-mono"
            maxLength={8}
          />
          <Button type="submit" disabled={dniLoading} data-testid="arca-dni-btn" variant="outline">
            {dniLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
            {dniLoading ? 'Buscando…' : 'Buscar CUIT'}
          </Button>
        </form>
        {dniResult && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="arca-dni-result">
            {(dniResult.cuits || []).length === 0 ? (
              <span className="text-sm text-slate-400">Sin resultados para ese DNI.</span>
            ) : (
              (dniResult.cuits || []).map((c) => (
                <button key={c} onClick={() => usarCuit(c)}
                  className="font-mono text-sm bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg px-2.5 py-1"
                  data-testid={`arca-dni-cuit-${c}`}>
                  {c}
                </button>
              ))
            )}
          </div>
        )}
      </Card>

      {error && (
        <Card className="p-5 border-amber-200 bg-amber-50" data-testid="arca-consulta-error">
          <div className="flex items-start gap-3 text-amber-800">
            <AlertTriangle size={18} className="mt-0.5" />
            <div>
              <p className="font-medium">{error}</p>
              {/certificado|clave|CUIT representada/i.test(error) && (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/m/arca/configuracion')}>
                  Ir a Configuración
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-6" data-testid="arca-consulta-result">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-sky-600" />
                <h2 className="text-lg font-semibold text-slate-900">{r.razon_social || 'Contribuyente'}</h2>
              </div>
              <p className="font-mono text-sm text-slate-500 mt-1">{r.cuit}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              {r.estado && <Badge className={r.estado === 'ACTIVO' ? 'bg-emerald-600' : 'bg-slate-400'}>{r.estado}</Badge>}
              {result.cached && <span className="text-xs text-slate-400">desde cache</span>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-x-8">
            <div>
              <InfoRow label="Tipo de persona" value={r.tipo_persona} />
              <InfoRow label="Condición IVA" value={r.condicion_iva} />
              {r.monotributo && <InfoRow label="Categoría Monotributo" value={r.monotributo.categoria} />}
              <InfoRow label="Mes de cierre" value={r.mes_cierre} />
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1 mt-1">
                <MapPin size={14} /> Domicilio fiscal
              </div>
              <p className="text-sm text-slate-800">{dom.direccion || '—'}</p>
              <p className="text-sm text-slate-500">{[dom.localidad, dom.provincia, dom.codigo_postal].filter(Boolean).join(' · ')}</p>
            </div>
          </div>

          {actividadesDe(r).length > 0 && (
            <div className="mt-4" data-testid="arca-consulta-actividades">
              <p className="text-sm font-medium text-slate-700 mb-1">Actividad declarada</p>
              <ul className="text-sm text-slate-600 space-y-1">
                {actividadesDe(r).slice(0, 8).map((a, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {a.principal && <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px] px-1.5 py-0">Principal</Badge>}
                    <span>{a.desc} {a.cod ? <span className="text-slate-400">({a.cod})</span> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(r.impuestos) && r.impuestos.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-slate-700 mb-1">Impuestos</p>
              <div className="flex flex-wrap gap-2">
                {r.impuestos.map((imp, i) => (
                  <Badge key={i} variant="outline" className="text-slate-600">{imp.descripcion || imp.id}</Badge>
                ))}
              </div>
            </div>
          )}

          <button
            className="mt-5 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            onClick={() => setShowRaw(!showRaw)}
            data-testid="arca-toggle-raw"
          >
            {showRaw ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <FileText size={14} /> Ver respuesta completa (JSON)
          </button>
          {showRaw && (
            <pre className="mt-2 max-h-96 overflow-auto text-xs bg-slate-900 text-slate-100 p-4 rounded-lg">
              {JSON.stringify(result.raw, null, 2)}
            </pre>
          )}
        </Card>
      )}
        </TabsContent>

        <TabsContent value="masivo" className="mt-4">
          <BulkSearch
            accent="sky"
            fetchOne={(c) => api.get(`/arca/consulta/${c}`).then((res) => res.data)}
            summaryHeaders={['Razón social', 'Estado', 'Condición IVA']}
            renderSummaryCells={(d) => { const r = d?.resumen || {}; return [r.razon_social || '—', r.estado || '—', r.condicion_iva || '—']; }}
            renderDetail={(d) => <PadronDetalle data={d} />}
            onConfigError={() => navigate('/m/arca/configuracion')}
            exportConfig={{
              headers: ['CUIT', 'Razón social', 'Tipo persona', 'Estado', 'Condición IVA', 'Monotributo', 'Actividad principal', 'Cód. act. principal', 'Todas las actividades', 'Impuestos', 'Domicilio', 'Localidad', 'Provincia', 'CP', 'Resultado'],
              row: (r) => {
                if (r.status !== 'ok') return [r.cuit, ...Array(13).fill(''), r.status === 'error' ? `ERROR: ${r.error}` : 'sin buscar'];
                const d = r.data?.resumen || {}; const dom = d.domicilio || {};
                const acts = actividadesDe(d); const pr = acts.find((a) => a.principal) || acts[0] || {};
                const impuestos = (d.impuestos || []).map((i) => i.descripcion || i.id).join(' | ');
                const todas = acts.map((a) => `${a.desc}${a.cod ? ` (${a.cod})` : ''}`).join(' | ');
                return [r.cuit, d.razon_social || '', d.tipo_persona || '', d.estado || '', d.condicion_iva || '', d.monotributo?.categoria || '', pr.desc || '', pr.cod || '', todas, impuestos, dom.direccion || '', dom.localidad || '', dom.provincia || '', dom.codigo_postal || '', 'OK'];
              },
            }}
          />
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <HistorialPanel service="padron" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
